/**
 * @fileoverview Companies view list component
 * 
 * This component renders the list of saved views for companies data.
 * It allows users to select, add, and delete lists.
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/viewMenu/index.tsx} - View menu component
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/DeleteNewViewModal.tsx} - Delete view modal component
 * @see {@link src/hooks/useCompaniesListRequest.ts} - Hook for companies list data fetching
 * @see {@link src/store/newLists/newLists.slectors.ts} - Selectors for lists state
 */

import { useMemo, useState } from "react";

import { Flex, Icon, useDisclosure } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import InfoIcon from "@/assets/svg/list-info-icon.svg";
import { useActions } from "@/hooks/useActions";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import { useDeleteListMutation } from "@/services/newList.service";
import newListSelectors from "@/store/newLists/newLists.slectors";

import DeleteNewViewModal from "../DeleteNewViewModal";
import ListText from "../shared/ListText";
import ViewMenu from "../viewMenu";

import AddListModal from "./AddListModal";

/**
 * List item interface for company lists
 * @see src/components/companies/companiesList/newCompaniesList/viewListTools/viewMenu/index.jsx
 * @see src/store/newLists/newLists.slectors.ts
 */
interface ListItem {
  /**
   * Unique identifier for the list
   */
  id: string;
  
  /**
   * Display title for the list
   */
  title: string;
  
  /**
   * Optional additional properties
   * Using Record<string, unknown> instead of any for better type safety
   */
  [key: string]: string | number | boolean | object | undefined | null;
}

const defaultList: ListItem[] = [{ id: "all companies", title: "All Companies" }];

/**
 * Companies view list component
 * Displays a list of saved company views and allows for selection, addition, and deletion
 */
const CompaniesViewList = () => {
  const controlAddListModal = useDisclosure();
  const deleteViewModalControl = useDisclosure();
  const { setCurrentNewList, setViewParams } = useActions();
  const { currentParams, currentView } = useGetCurrentView();
  const currentList = useSelector(newListSelectors.getCurrentList());
  const [selectedListToDelete, setSelectedListToDelete] = useState<ListItem | null>(null);
  const [deleteList] = useDeleteListMutation();
  const { getCompanies } = useCompaniesListRequest();

  const lists = useSelector(newListSelectors.getAllLists());

  const allLists = useMemo<ListItem[]>(() => {
    return [...defaultList, ...lists];
  }, [lists]);

  /**
   * Opens the delete modal and sets the selected list to delete
   * 
   * @param view - The list item to delete
   */
  const onDeleteListModalOpen = (view: ListItem) => {
    deleteViewModalControl.onOpen();
    setSelectedListToDelete(view);
  };

  /**
   * Handles the deletion of a list
   */
  const onDeleteListHandler = () => {
    if (selectedListToDelete) {
      deleteList(selectedListToDelete.id);
      deleteViewModalControl.onClose();
    }
  };

  /**
   * Handles the selection of a list and fetches companies for that list
   * 
   * @param list - The selected list item
   */
  const onGetCompaniesBylist = (list: ListItem) => {
    setCurrentNewList(list);
    setViewParams({ key: currentView?.id, params: { text: undefined } });
    getCompanies(
      {
        params: {
          ...currentParams,
          orderBy: currentParams?.orderBy?.value,
          page: 1,
          limit: 25,
          text: undefined,
        },
      },
      list.id
    );
  };

  return (
    <>
      <DeleteNewViewModal
        deleteViewModalControl={deleteViewModalControl}
        deleteHandler={onDeleteListHandler}
        selectedItemToDelete={selectedListToDelete || { title: "" }}
      />
      <AddListModal
        onClose={controlAddListModal.onClose}
        isOpen={controlAddListModal.isOpen}
        title={"Add list"}
        isView={false}
      />
      <Flex
        w={"100%"}
        align={"start"}
        minH={"40px"}
        h={"100%"}
        px={"8px"}
        py={"4px"}
        minW={{ base: "100%", md: "400px" }}
        borderRadius={"8px"}
        bg={"lightGreen2"}
      >
        <Flex align={"center"} h={"32px"} mr={"8px"}>
          <ListText title={"Data"} />
          <Icon as={InfoIcon} alignSelf={"flex-start"} />
        </Flex>
        <ViewMenu
          selected={currentList}
          onAddOpen={controlAddListModal.onOpen}
          listData={allLists}
          listHandler={onGetCompaniesBylist}
          deleteHandler={onDeleteListModalOpen}
          isView={false}
        />
      </Flex>
    </>
  );
};

export default CompaniesViewList;
